import Vue from 'vue';
import { append } from '@polyv/utils/src/querystring';
import { getCurrentUAInfo } from '@just4/ua-info';
import basicMixin from '../../../assets/mixins/basic';
import { isMobile } from '@polyv/utils/src/browser';
import { BuyType } from './constants';
import wechatMixin from '@/assets/mixins/wechat';
import { ynToBool } from '@polyv/utils/src/boolean';
import MobilePopup from '@/components/product/popup-link/MobilePopupLink.vue';
import PcPopup from '@/components/product/popup-link/PcPopupLink.vue';

const uaInfo = getCurrentUAInfo();

/**
 * @emit disabled-status-change
 */
export default {
  inject: ['productVm'],

  components: {
    MobilePopup,
    PcPopup
  },

  mixins: [basicMixin, wechatMixin],

  props: {
    good: Object,
    /** 是否显示禁用状态的节点 */
    showDisabled: {
      type: Boolean,
      default: true,
    },
    /** 是否覆盖父容器的整个区域（父容器要设为 position: relative） */
    isCoverAll: {
      type: Boolean,
      default: false
    }
  },

  data() {
    return {
      // 是否移动端
      isMobile: uaInfo.isPortable,
      // 是否安卓系统
      isAndroid: uaInfo.os.isAndroid,
      // 是否 iOS 系统
      isIOS: uaInfo.os.isIOS,
      // 弹框打开的外链地址
      popupPageHref: ''
    };
  },

  computed: {
    productSdk() {
      return this.productVm.productSdk;
    },

    buttonText() {
      if (!this.good) {
        return this.iarTrans('product.buyFinance');
      }

      const { btnShow, productType } = this.good;
      const defaultText = productType === 'finance' ? this.iarTrans('product.buyFinance') : productType === 'position' ? this.iarTrans('product.deliverResume') : this.iarTrans('product.buy');
      return btnShow || defaultText;
    },

    /**
     * 链接类型
     */
    linkType() {
      if (!this.good) {
        return;
      }
      return Number(this.good.linkType || 10);
    },

    /**
      * 判断是否显示微信小程序按钮
      * 1. 微信浏览器环境
      * 2. 填写了原始 id
      * 3. 微信版本高于 7.0.12
      */
    isWeixinButton() {
      if (!this.good || !isMobile()) {
        return false;
      }

      // 如果传入自定义处理点击事件则小程序按钮失效
      if (typeof this.productVm?.customClickButtonHandler === 'function') {
        return false;
      }

      // 小程序 webview 内使用原生 sdk 功能跳转
      if (this.isWxMiniProgramWebview) {
        return false;
      }

      return (
        this.isWeixin &&
        this.good.wxMiniprogramOriginalId &&
        this.isHighWeixinVersion
      );
    },

    /**
     * 多平台场景下是否禁用
     */
    isMultiPlatformDisabled() {
      if (!this.good) {
        return true;
      }

      const { pcLink, mobileLink, wxMiniprogramLink, mobileAppLink } = this.good;
      // 支持 H5 微信小程序跳转优先级最高
      if (this.isWeixinButton) {
        return false;
      }

      // 当前在微信小程序 webview 中且商品存在 wxMiniprogramLink 字段
      if (this.isWxMiniProgramWebview && wxMiniprogramLink) {
        return false;
      }

      // 移动 Web 下跳转
      if (this.isMobile) {
        return !mobileLink && !mobileAppLink;
      }

      // PC 下跳转
      if (!this.isMobile) {
        return !pcLink;
      }
    },

    isSoldOut() {
      if (this.good.productType === 'normal') {
        if (this.good.buyType === 'link') {
          return this.good.markAsSoldout === 'Y';
        }
        if (this.good.buyType === 'inner' && this.good.stockEnabled === 'Y') {
          return this.good.stock <= 0;
        }
      }
      return false;
    },

    /**
      * 判断按钮是否为禁用状态
      */
    isButtonDisabled() {
      if (!this.good) {
        return true;
      }

      if (typeof this.productVm?.customProductDisabled === 'function') {
        const isDisabled = this.productVm.customProductDisabled(this.good);
        return !!isDisabled;
      }

      if (this.good.productBuyButtonPreviewMode) {
        return false;
      }

      if (!this.productSdk) {
        return true;
      }

      if (this.isSoldOut) {
        return true;
      }

      const ProductLinkType = this.productSdk.ProductLinkType;
      const ProductType = this.productSdk.ProductType;
      const { link, androidLink, iosLink, otherLink, buyType, productType, realPrice } = this.good;

      // 普通产品如果未开价，按钮禁用(不包含免费商品)
      if (productType === ProductType.Normal && !ynToBool(this.good.openPriceEnable || 'Y') && realPrice > 0) {
        return true;
      }

      // 直接购买
      if (buyType === BuyType.Inner) {
        return false;
      }

      // 通用平台
      if (this.linkType === ProductLinkType.Normal) {
        return !link;
      }

      // 多平台链接
      if (this.linkType === ProductLinkType.MultiPlatform) {
        return this.isMultiPlatformDisabled;
      }

      // 原生方法
      if (this.linkType === ProductLinkType.Native) {
        // 如果当前环境支持原生方法，则根据 androidLink 和 iosLink 判断
        if (this.supportNativeToPointMall()) {
          return !androidLink && !iosLink;
        }
        return !otherLink;
      }

      return false;
    },

    getWeixinSdk() {
      return this.productVm?.getWeixinSdk;
    }
  },

  watch: {
    good: {
      immediate: true,
      handler() {
        this.$nextTick(() => {
          if (this.isWeixinButton && this.$refs.wxbtn) {
            const width = this.$el.clientWidth;
            const height = this.$el.clientHeight;

            if (width && height) {
              this.createWxMiniProgramButton({
                wrap: this.$refs.wxbtn,
                wxMiniprogramOriginalId: this.good.wxMiniprogramOriginalId,
                wxMiniprogramLink: this.formatProductLink(this.good.wxMiniprogramLink),
                classKey: 'product',
                width,
                height,
                lanuchCallback: () => {
                  this.trackProduct();
                  this.handleClickBuy();
                },
              });
            } else {
              console.error('Cannot create createWxMiniProgramButton due to element width and height are both 0');
            }
          }
        });
      },
    },

    popupPageHref: {
      immediate: true,
      handler(value) {
        if (value) {
          this.$nextTick(() => {
            const Popup = Vue.extend(this.isMobile ? MobilePopup : PcPopup);
            const popup = new Popup({
              propsData: {
                href: value
              }
            });
            popup.$on('change', (value) => {
              this.popupPageHref = value;
            });
            popup.$mount();
            document.body.appendChild(popup.$el);
            this.$options.popupComponent = popup;
          });
        } else {
          const popup = this.$options.popupComponent;
          if (popup) {
            popup.$destroy();
            popup.$el.parentNode.removeChild(popup.$el);
            this.$options.popupComponent = null;
          }
        }
      },
    },

    isButtonDisabled: {
      immediate: true,
      handler() {
        this.$emit('disabled-status-change', this.isButtonDisabled);
      },
    },
  },

  methods: {
    /**
     * 调用 window.open 打开一个地址
     * @param {String} url 地址
     */
    openLink(url, isNewWindow) {
      if (isNewWindow || this.good.jumpWay === 'NEW_WINDOW' || !this.good.jumpWay) {
        window.open(url, '_blank', 'noopener=yes');
      } else {
        window.location.href = url;
      }
    },

    /**
     * 处理商品链接打开路径
     * @param {String} url 地址
     * @param {Object} params 参数
     */
    formatProductLink(url, params = {}) {
      let urlParams = {
        ...params,
      };

      // 通过组件入参传入额外的参数
      if (typeof this.productVm?.getLinkParams === 'function') {
        const res = this.productVm.getLinkParams(url);
        if (typeof res === 'object') {
          urlParams = Object.assign({}, urlParams, res);
        }
      }

      return append(url, urlParams, { ignoreEmpty: false });
    },

    handleClickBuy() {
      !this.isButtonDisabled && this.productVm.emitClickBuy(this.good);
    },

    /**
     * 处理按钮点击，已排除按钮的禁用状态、微信原生按钮跳转小程序场景
     */
    async clickBtn() {
      if (!this.good) {
        return;
      }

      this.trackProduct();

      // 组件传入了 customClickButtonHandler 则直接调用该方法，终止程序
      if (typeof this.productVm?.customClickButtonHandler === 'function') {
        this.productVm.customClickButtonHandler(this.good);
        return;
      }

      if (!this.productSdk) {
        return;
      }

      const { buyType } = this.good;
      // 直接购买
      if (buyType === 'inner') {
        this.productVm.emitBuy(this.good);
        return;
      }

      const ProductLinkType = this.productSdk.ProductLinkType;

      // 原生方法跳转
      if (this.linkType === ProductLinkType.Native) {
        this.toNativeLink();
        return;
      }

      // 保利威 SDK Webview 下跳转
      if (this.supportPlvWebviewBridage()) {
        this.toPlvWebviewBridage();
        return;
      }

      // 通用平台
      if (this.linkType === ProductLinkType.Normal) {
        const { link, jumpWay } = this.good;
        const href = this.formatProductLink(link);
        if (jumpWay === 'POP_UP') {
          this.popupPageHref = href;
        } else {
          this.openLink(href);
        }
        return;
      }

      // 多平台跳转
      if (this.linkType === ProductLinkType.MultiPlatform) {
        const { wxMiniprogramLink, mobileLink, pcLink } = this.good;

        // 小程序 webview 环境，利用微信 sdk 跳转指定页面
        if (this.isWxMiniProgramWebview && wxMiniprogramLink) {
          this.toWxMiniProgramNavigate();
          return;
        }

        // 移动 Web 跳转
        if (this.isMobile) {
          this.openLink(this.formatProductLink(mobileLink), true);
          return;
        }

        // PC 跳转
        if (!this.isMobile) {
          this.openLink(this.formatProductLink(pcLink), true);
          return;
        }
      }

      console.warn('linkType not support');
    },
    /**
     * 从列表点击商品购买按钮或者从推送卡片点击按钮都触发埋点
     */
    trackProduct() {
      if (!this.productSdk) return;

      const specAttrs = this.getRtasSpecAttrs();
      const trackLogData = {
        event_cata: 'behavior',
        event_type: 'click',
        event_id: 'product_click_button',
        spec_attrs: specAttrs,
      };
      this.productSdk.trackLog(trackLogData);
    },

    getRtasSpecAttrs() {
      if (!this.good) return {};
      return {
        buyType: this.good.buyType,
        deliveryType: this.good.deliveryType,
        name: this.good.name,
        productId: this.good.productId,
        productType: this.good.productType,
        productPushRule: this.good.productPushRule,
      };
    },

    /** ------------ 保利威 SDK webview 跳转 start ------------ */
    /**
     * 获取保利威 SDK 桥接器对象
     */
    getPlvWebviewBridage() {
      if (typeof this.productVm.getPlvWebviewBridge === 'function') {
        return this.productVm.getPlvWebviewBridge();
      }
    },

    /**
     * 是否支持保利威 SDK 桥接器跳转
     */
    supportPlvWebviewBridage() {
      return this.productVm.innerIsPlvWebview && !!this.getPlvWebviewBridage();
    },

    /**
     * 调用保利威 SDK 桥接器跳转链接
     */
    toPlvWebviewBridage() {
      if (!this.productSdk || !this.good) {
        return;
      }
      if (!this.supportPlvWebviewBridage()) {
        return;
      }
      const ProductLinkType = this.productSdk.ProductLinkType;

      let linkTo = '';
      const { link, mobileLink, mobileAppLink, wxMiniprogramOriginalId, wxMiniprogramLink } = this.good;
      switch (this.linkType) {
        case ProductLinkType.Normal:
          linkTo = link;
          break;
        case ProductLinkType.MultiPlatform:
          linkTo = mobileAppLink || mobileLink;
          break;
      }

      // 非通用链接，将其余字段单独放到 data 供客户使用
      let productData = null;
      if (this.linkType !== ProductLinkType.Normal) {
        productData = {
          mobileLink: this.formatProductLink(mobileLink),
          wxMiniprogramOriginalId,
          wxMiniprogramLink: this.formatProductLink(wxMiniprogramLink),
          mobileAppLink: this.formatProductLink(mobileAppLink),
        };
      }
      const plvWebviewDataSize = this.productVm.plvWebviewDataSize;
      // 发送到 SDK 的数据
      const webviewData = {
        width: plvWebviewDataSize.width,
        height: plvWebviewDataSize.height,
        newPage: true,
        link: this.formatProductLink(linkTo),
        data: productData,
      };
      const webviewBridage = this.getPlvWebviewBridage();
      webviewBridage?.sendData('clickProduct', webviewData);
      this.productVm.emitClickPlvWebviewJump(this.good);
    },
    /** ------------ 保利威 SDK webview 跳转 end ------------ */

    /** ------------ 小程序 webview 页跳转 start ------------ */

    /**
     * 调用微信 sdk 方法跳转到当前小程序的某个页面
     */
    async toWxMiniProgramNavigate() {
      if (!this.good) {
        return;
      }
      const url = this.formatProductLink(this.good.wxMiniprogramLink);
      await this.toWxMiniProgram(url);
    },
    /** ------------ 小程序 webview 页跳转 end ------------ */

    /** ------------ 原生方法跳转 start ------------ */
    /**
     * 获取原生 App 方法
     */
    getNativeToPointMallFn() {
      if (this.isAndroid) {
        return window.AndroidNative?.toPointMall;
      } else if (this.isIOS) {
        return window.webkit?.messageHandlers?.gotoPointsMall?.postMessage;
      }
    },

    /**
     * 是否支持原生 App 方法
     */
    supportNativeToPointMall() {
      return !!this.getNativeToPointMallFn();
    },

    /**
     * 调用原生方法跳转
     */
    toNativeLink() {
      if (!this.good) {
        return;
      }
      const { androidLink, iosLink, otherLink } = this.good;

      const nativePointMallFn = this.getNativeToPointMallFn();
      if (nativePointMallFn) {
        let url = this.isAndroid ? androidLink : iosLink;

        // 处理地址并注入 plt_back_uri
        const pltBackUri = encodeURIComponent(location.href);
        url = this.formatProductLink(url, {
          plt_back_uri: pltBackUri,
        });

        const paramsStr = JSON.stringify({
          url
        });
        nativePointMallFn(paramsStr);
        return;
      }

      this.openLink(this.formatProductLink(otherLink));
    },
    /** ------------ 原生方法跳转 end ------------ */
  },
};
